import type { HTMLAttributes } from 'react';
import React from 'react';
import { NotFoundError } from 'restify-errors';
import classNames from 'classnames';
import { merge, pickBy, uniq } from 'lodash';
import { formatAmountWithCurrency } from '@fixter/i18n';
import type { PriceData } from '@fixter/model-vehicle-pricing-tables/ProductPriceData';
import type { ContentComponent, ContentComponentFetchServerSideData } from '$cms/ContentComponent';
import { getRequestId, getServerLogger } from '$util/logger';
import { capitalize } from '$util/basic/capitalize';
import { getSegmentFromLocale } from '$util/getSegment';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { extendCmsText } from '$cms/index';
import { useCmsPageContext } from '$cms/CmsPageContext';
import { VrmLookupModal } from '$quantum/molecules/VrmLookupModal';
import type { RedisStore } from '@/server/stores/RedisStore';
import { ContentComponentSection } from '@/components/atoms/ContentComponentSection';
import { Col, Row } from '@/components/atoms/Grid';
import { expandProductGroupSkus, getGroupProductSkus } from './helpers/getGroupProductSkus';
import { makeSkuMakeModelSets } from './helpers/makeSkuMakeModelSets';
import { renderDescription } from './helpers/renderDescription';
import {
  fetchCarMakeModels,
  fetchPopularCarMakeModels,
  fetchPopularCarMakes,
  fetchPopularProducts,
} from './helpers/fetch-collections';
import type {
  PricingTableServerSideData,
  PricingTableTranslatedEntities,
  PricingTablesConfiguration,
} from './schema';
import { PricingTableSchema, defaultConfiguration } from './schema';
import { pickConsistentRandom } from './helpers/pickConsistentRandom';
import { buildPriceRangeRows } from './helpers/buildPriceRangeRows';

import styles from './index.module.scss';

export interface PricingTableProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

/**
 * A content component that displays pricing tables
 * https://www.notion.so/fixter/PricingTable-660ab2457ebe45449a3e44c95464a060?pvs=4
 */

export const PricingTable: ContentComponent<
  PricingTableTranslatedEntities,
  PricingTablesConfiguration,
  PricingTableProps,
  PricingTableServerSideData
> = function PricingTable({
  translatedEntities,
  configuration,
  serverSideData,
  className,
  ...props
}): JSX.Element | null {
  const { locale } = useAppContext<AppContextGlobal>();
  const cmsPageContext = useCmsPageContext();
  if (!cmsPageContext || !serverSideData) return null;

  const {
    pageRoutes: [{ path: currentPath, collectionItems }],
  } = cmsPageContext;

  if (!collectionItems) return null;

  const { unitSkus, pricesArray } = serverSideData;
  const unitSkusPriceRows = collectionItems.product?.sku
    ? pricesArray.filter(({ sku }) => unitSkus.includes(sku))
    : [];

  const { excludePaths } = merge(defaultConfiguration, configuration);
  if (excludePaths.some((path) => path === currentPath)) return null;

  const placeholders = pickBy(
    {
      skuName: collectionItems.product?.name,
      areaName: collectionItems.area?.name,
      carMakeName: collectionItems.carMake?.name,
      carModelName: collectionItems.carModel?.name,
    },
    (val): val is string => typeof val !== 'undefined'
  );

  const segment = getSegmentFromLocale(locale);
  const {
    makeModelColumn,
    priceRangeColumn,
    getQuoteButtonText,
    saveLabel,
    title,
    description,
    descriptionAdditionalSku,
    footerText,
    forText,
  } = translatedEntities;

  return (
    <ContentComponentSection
      className={classNames(PricingTable.displayName, styles.component, className)}
      {...props}
    >
      <Row>
        <Col>
          <h2>{extendCmsText(title.value, placeholders)}</h2>
          <div className={styles.description}>
            {renderDescription(
              [description.content, descriptionAdditionalSku?.content],
              unitSkusPriceRows,
              placeholders,
              segment
            )}
          </div>
          <table>
            <tbody>
              <tr>
                <th className={styles.leftCol}>{makeModelColumn.value}</th>
                <th className={styles.rightCol}>{priceRangeColumn.value}</th>
              </tr>
              {pricesArray.map((price) => {
                const {
                  makeName,
                  modelName,
                  highestPrice,
                  lowestPrice,
                  saveAmount,
                  skuName: productName,
                } = price;
                const productMakeModel = `${capitalize(productName)} ${
                  forText.value
                } ${makeName} ${modelName}`;
                const saveAmountValue = `${saveLabel.value} ${formatAmountWithCurrency(
                  saveAmount / 100,
                  segment,
                  0
                )}`;
                const priceRange = `${formatAmountWithCurrency(
                  lowestPrice / 100,
                  segment,
                  0
                )} - ${formatAmountWithCurrency(highestPrice / 100, segment, 0)}`;
                return (
                  <tr key={productMakeModel}>
                    <td className={styles.leftCol}>{productMakeModel}</td>
                    <td
                      className={classNames(styles.rightCol, styles.priceRange)}
                      data-content={saveAmountValue}
                    >
                      {priceRange}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className={styles.footerText}>{footerText.value}</p>
          <div className={styles.getQuote}>
            <VrmLookupModal
              id="VrmLookupModalPricingTable"
              toggleButton={{ size: 'M', children: getQuoteButtonText.value }}
            />
          </div>
        </Col>
      </Row>
    </ContentComponentSection>
  );
};
PricingTable.displayName = 'PricingTable';
PricingTable.schema = PricingTableSchema;

export interface PricingTableParams {
  readonly productSku?: string;
  readonly make?: string;
  readonly model?: string;
  readonly area?: string;
}

export const fetchServerSideData: ContentComponentFetchServerSideData<PricingTableServerSideData> = async (
  { segment, pageRoutes: [{ path, collectionItems }] },
  preview,
  container,
  req
) => {
  const log = getServerLogger(`${PricingTable.displayName}:fetchServerSideData ${path}`).child({
    requestId: getRequestId(req, 'Landing'),
  });

  /**
   * Can be a single SKU, a ProductGroup SKU or multiple ones separated by semicolon.
   */
  const productCollectionItemSku = collectionItems?.product?.sku;

  const params: PricingTableParams = {
    productSku: productCollectionItemSku,
    make: collectionItems?.carMake?.id,
    model: collectionItems?.carModel?.id,
    area: collectionItems?.area?.id,
  };

  log.info(params, 'Fetching pricing table data');

  try {
    const groupProducts = await getGroupProductSkus(segment, req.headers);
    let unitSkus: string[] = [];
    if (productCollectionItemSku)
      unitSkus = expandProductGroupSkus([productCollectionItemSku], groupProducts);

    const redisStore = container.get<RedisStore>('RedisStore');
    const getPricesData = async (sets: ReturnType<typeof makeSkuMakeModelSets>): Promise<PriceData[]> => {
      const keys = sets.map(
        ([make, model, sku]) => `vehicle-pricing-table-${segment}:${make}:${model}:${sku}`
      );
      const prices = await redisStore
        .getJSONDocuments<(PriceData[] | null)[]>(keys, '$')
        .then((json) => json?.flatMap((data) => data ?? []) ?? []);
      return prices;
    };

    let sets: [sku: string, make: string, model: string][] = [];

    if ('make' in params && params.make) {
      const { make, model } = params;
      if (model) {
        /**
         * For /product/make/model pages we have only one row (sku, make, model) so we'll get the top products for more rows.
         * For /make/model pages we need the top products to display with the existing make and model
         */
        const popularSkus = await fetchPopularProducts(segment, preview).then((products) =>
          expandProductGroupSkus(
            products.map((product) => product.sku),
            groupProducts
          )
        );
        if (!popularSkus.length) {
          throw new NotFoundError({ info: params }, 'Popular products data not found');
        }

        const uniqSkus = uniq([...unitSkus, ...popularSkus]);
        sets = makeSkuMakeModelSets(uniqSkus, new Map([[make, [model]]]));
      } else {
        /**
         * SKU combined with one make and it's corresponding popular models
         */
        const models = await fetchCarMakeModels(make, preview).then((res) => res.map(({ id }) => id));
        if (!models.length) {
          throw new NotFoundError({ info: params }, 'Make models data not found');
        }

        sets = makeSkuMakeModelSets(unitSkus, new Map([[make, models]]));
      }
    } else if ('area' in params && params.area) {
      /**
       * SKU combined with area, show popular makes with one random model
       */
      const { area } = params;

      const popularMakes = await fetchPopularCarMakes(segment, preview).then((res) =>
        res.map(({ id }) => id)
      );
      if (!popularMakes.length) {
        throw new NotFoundError({ info: params }, 'Popular car makes data not found');
      }

      const cars: Map<string, string[]> = await Promise.all(
        popularMakes.map((make) => fetchCarMakeModels(make, preview))
      ).then((carMakeModels) => {
        const pickRandom = pickConsistentRandom(`${productCollectionItemSku}:${area}`);
        return carMakeModels.reduce((cm, models) => {
          const model = pickRandom(models);
          if (model) cm.set(model.make.id, [model.id]);
          return cm;
        }, new Map<string, string[]>());
      });
      if (!cars.size) {
        throw new NotFoundError({ info: params }, 'Models for popular car makes data not found');
      }

      sets = makeSkuMakeModelSets(unitSkus, cars);
    } else if ('productSku' in params && params.productSku) {
      /**
       * only SKU, show popular makes with top model
       */
      const cars: Map<string, string[]> = await fetchPopularCarMakeModels(segment, preview).then((models) =>
        models.reduce((cm, { id, make }) => cm.set(make.id, [id]), new Map<string, string[]>())
      );
      if (!cars.size) {
        throw new NotFoundError({ info: params }, 'Popular car make models data not found');
      }

      sets = makeSkuMakeModelSets(unitSkus, cars);
    } else {
      log.error('Unsupported parameters for pricing tables!');
      return null;
    }

    // limit to 20 keys to have enough data to pick from if some sets don't have prices
    const prices = await getPricesData(sets.slice(0, 20));

    if (!prices.length) {
      log.warn({ params }, 'No prices found');
      return null;
    }
    /**
     * @note sometimes there are no prices for the product skus
     * and want to avoid displaying only unrelated products prices.
     */
    if ('productSku' in params && params.productSku) {
      const hasProductPrices = prices.some(({ sku }) => unitSkus.includes(sku));
      if (!hasProductPrices) {
        log.warn({ params, unitSkus }, 'No prices found for product');
        return null;
      }
    }

    const getPricesArrayLength = () => {
      if (!('productSku' in params) || !params.productSku) {
        return 10;
      }
      if (unitSkus.length > 1) return 12;
      return 7;
    };

    const pricesArray = buildPriceRangeRows(prices).slice(0, getPricesArrayLength());
    return PricingTableSchema.serverSideData.parse({ unitSkus, pricesArray });
  } catch (error) {
    if (error instanceof NotFoundError) log.debug(error);
    else log.error(error);
    return null;
  }
};
